<script setup lang="ts">
import {computed} from "vue";
import FChip, {FChipProps} from "@/lib/components/ui/FChip.vue";
import {getTagsTextColor} from "@/tscript/utils/schedulingUtils";
import {type SchedulingTag} from "@oplit/shared-module";
import {useSchedulingTag} from "@/composables/scheduling/useSchedulingTag";
import {useI18n} from "vue-i18n";

interface SchedulingTagChipProps extends FChipProps {
  tag: string | SchedulingTag;
  forceDisplay?: boolean;
  fromImport?: boolean;
}

const props = withDefaults(defineProps<SchedulingTagChipProps>(), {
  forceDisplay: false,
  medium: true,
  large: false,
  fromImport: false,
});

const {t} = useI18n();

const emit = defineEmits<{
  (e: "remove", tag: Partial<SchedulingTag>);
}>();

const {getSchedulingTag} = useSchedulingTag();

const computedTag = computed<SchedulingTag | Partial<SchedulingTag>>(() =>
  getSchedulingTag(props.tag, props.forceDisplay),
);
</script>

<template>
  <FChip
    v-if="computedTag"
    :color="computedTag.color_name"
    :text-color="getTagsTextColor(computedTag.color_name)"
    :close="!fromImport && close"
    :medium="medium"
    class="scheduling-tag-chip"
    show-tooltip
    :tooltip-array="fromImport ? [t('scheduling.imported_tag')] : null"
    :data-testid="`scheduling-scheduling-tag-chip-${computedTag.label}`"
    @close="() => emit('remove', computedTag)"
  >
    <slot>
      <span class="fd-flex-center gap-2">
        <span
          class="text-neutral-secondary text-elipsis"
          :data-testid="`scheduling-scheduling-tag-chip-${computedTag.label}-label`"
        >
          {{ computedTag.label }}
        </span>
        <span class="text-neutral-tertiary">
          <OpalIcon v-if="fromImport" name="file-arrow-up" />
        </span>
      </span>
    </slot>
  </FChip>
</template>

<style scoped lang="scss">
.scheduling-tag-chip {
  max-width: 150px;
  & button {
    // close icon size
    font-size: 14px !important;
  }
}
</style>
